import { useEffect } from "react";

import { loadPosthog, posthog, posthogLoaded } from "@/lib/posthog";

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST || "https://eu.i.posthog.com";

/**
 * Consent gate: read the cc-consent localStorage contract written by
 * the cookie banner (see useCookieConsent). We react to the
 * `cc-consent-updated` window event so opt-in / opt-out take effect
 * immediately — no page reload required.
 */
function hasAnalyticsConsent(): boolean {
  try {
    const raw = localStorage.getItem("cc-consent");
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { analytics?: unknown };
    return parsed.analytics === true;
  } catch {
    return false;
  }
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (!POSTHOG_KEY) return;

    // El paquete se descarga aquí, solo con consentimiento (src/lib/posthog.ts).
    const init = async () => {
      const ph = await loadPosthog();
      if (!ph.__loaded) {
        ph.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          capture_pageview: false,
          capture_pageleave: true,
          persistence: "localStorage+cookie",
          // Session replay records every DOM mutation; the virtualized
          // cartelera mutates constantly while scrolling and replay makes the
          // page degrade progressively. Keep it off regardless of the remote
          // project setting.
          disable_session_recording: true,
          // Start opted-out; we flip it explicitly based on consent so
          // refreshes never ship an event before we've read cc-consent.
          opt_out_capturing_by_default: true,
        });
        if (import.meta.env.DEV) {
          (window as unknown as { posthog?: typeof ph }).posthog = ph;
        }
      }
      ph.opt_in_capturing();
      // El primer $pageview (usePostHogPageview) espera a este aviso.
      window.dispatchEvent(new Event("posthog-ready"));
    };

    if (hasAnalyticsConsent()) {
      void init();
    }

    const onConsentChange = () => {
      if (hasAnalyticsConsent()) {
        void init();
      } else if (posthogLoaded()) {
        posthog().opt_out_capturing();
        posthog().reset();
      }
    };

    window.addEventListener("cc-consent-updated", onConsentChange);
    return () => {
      window.removeEventListener("cc-consent-updated", onConsentChange);
    };
  }, []);

  return <>{children}</>;
}
